// src/utils/staleChunk.js
// Detection + reload-once guard for stale lazy chunks. After a deploy, Vercel
// purges the previous build's hashed assets; a client still on the old
// index.html (or whose old service-worker precache was evicted when another
// tab accepted the update) then 404s on its next lazy route import.

const PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome / Edge
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /Unable to preload CSS/i, // Vite CSS preload
  /Expected a JavaScript.*module script/i, // wrong MIME (HTML served for a chunk)
];

export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const message = String(err.message ?? err);
  return PATTERNS.some((re) => re.test(message));
}

const RELOAD_KEY = 'prism_chunk_reload_at';
const RELOAD_WINDOW_MS = 60 * 1000;

/** Reload at most once per minute. Resolves true if a reload was triggered. */
export async function reloadOnceForStaleChunk() {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  } catch {
    // sessionStorage unavailable — fall through and reload
  }
  if (Date.now() - last < RELOAD_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore
  }
  // Best effort: make sure the reload is not served by the same stale
  // service worker. If a newer worker is already waiting, tell it to take
  // over (same SKIP_WAITING message UpdatePrompt uses); then ask the
  // registration to check for an update before we reload.
  try {
    const reg = await navigator.serviceWorker?.getRegistration?.();
    if (reg?.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    await reg?.update?.();
  } catch {
    // no SW support or update check failed — reload anyway
  }
  window.location.reload();
  return true;
}
